const { useEffect, useRef, useState } = require('react');
const { context } = require('app-context');
const { v4: uuid } = require('uuid');
const { default: Button } = require('@mui/material/Button');
const { default: TextField } = require('@mui/material/TextField');
const { default: Styled } = require('styled-components');
const ArtworkImages = require('./ArtworkImages');
const { default: Dialog } = require('@mui/material/Dialog');
const { default: DialogTitle } = require('@mui/material/DialogTitle');
const { default: DialogContent } = require('@mui/material/DialogContent');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: CloseIcon } = require('@mui/icons-material/Close');

const internals = {};

module.exports = function Trivia({ name = 'Nerpy', onReturn }) {

    const [round, setRound] = useState(null);
    const [open, setOpen] = useState(false);
    const [guess, setGuess] = useState('');
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [library, setLibrary] = useState(null);
    const [gameKey, setGameKey] = useState('');
    const [choosing, setChoosing] = useState(false);
    const active = useRef(true);
    const inFlight = useRef(false);
    const nextRequest = useRef(null);
    const controlsEnd = useRef(null);
    const content = useRef(null);
    const lastRound = useRef(null);
    const { Panel, Buttons, Prompt, EmojiClue, Artwork } = internals;
    useEffect(() => {

        active.current = true;
        context.api.nearpeer.nerpy.triviaGames?.().then((value) => {

            if (active.current) { setLibrary(value); setGameKey(value.featuredKey || ''); }
        }).catch(() => {});
        return () => { active.current = false; };
    }, []);

    useEffect(() => {

        if (open) {
            if (choosing || lastRound.current !== round?.id) {
                if (content.current) { content.current.scrollTop = 0; }
                lastRound.current = round?.id;
            }
            else { controlsEnd.current?.scrollIntoView?.({ block: 'nearest' }); }
        }
    }, [open, round, feedback, choosing]);

    const closeGame = () => {

        if (inFlight.current) { return; }
        setOpen(false);
        setFeedback('');
        setGuess('');
        onReturn?.();
    };

    const next = async (options = {}) => {

        if (inFlight.current) {
            return;
        }

        inFlight.current = true;
        setBusy(true);
        setFeedback('');
        if (!nextRequest.current) {
            nextRequest.current = { requestId: uuid(), ...(library && gameKey ? { gameKey } : {}), ...options };
        }

        try {
            const value = await context.api.nearpeer.nerpy.triviaNext(nextRequest.current);
            if (active.current) {
                setRound(value);
                setGuess('');
                setOpen(true);
                setChoosing(false);
                nextRequest.current = null;
            }
        }
        catch (failure) {
            if (active.current) {
                const status = failure?.response?.status || failure?.statusCode;
                if (status === 409) {
                    nextRequest.current = null;
                    setFeedback('That question is no longer available. Please start a new round.');
                }
                else {
                    setFeedback('The game could not be loaded. Please try again.');
                }
            }
        }
        finally {
            if (active.current) {
                inFlight.current = false;
                setBusy(false);
            }
        }
    };

    const action = async (kind) => {

        if (inFlight.current) {
            return;
        }

        inFlight.current = true;
        setBusy(true);
        setFeedback('');
        try {
            const value = await context.api.nearpeer.nerpy.triviaAction(round.id, { action: kind, ...(kind === 'guess' ? { guess: guess.trim() } : {}) });
            window.dispatchEvent(new Event('gamification-progress'));
            if (active.current) {
                setRound(value);
                if (value.correct === false) {
                    setFeedback('Not quite. Try again, ask for a hint, or reveal the answer.');
                }
                else if (value.correct === true) {
                    setFeedback('You got it!');
                }
            }
        }
        catch {
            if (active.current) {
                setFeedback('That action could not be confirmed. Please try again.');
            }
        }
        finally {
            if (active.current) {
                inFlight.current = false;
                setBusy(false);
            }
        }
    };

    if (!open) {
        return <div><Button variant='outlined' disabled={busy} onClick={() => {

            if (library) { setChoosing(true); setOpen(true); }
            else if (round) { setOpen(true); }
            else { next(); }
        }}>Play a quick game</Button><p role='status'>{feedback}</p></div>;
    }

    return <Dialog open fullWidth maxWidth='sm' onClose={closeGame} aria-labelledby='nerpy-game-title' scroll='paper'>
        <DialogTitle id='nerpy-game-title' sx={{ paddingRight: '56px' }}>{round?.game && !choosing ? round.game.title : choosing ? 'Choose a game' : 'Emoji Movies'}{!choosing && round?.questionNumber && <> · {round.questionNumber} of {round.questionCount}</>}
            <IconButton aria-label='Close game and return to chat' disabled={busy} onClick={closeGame} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers ref={content}><Panel aria-label={`${name} trivia`}>
        {!choosing && round?.statistics && <p role='status' aria-label={round.complete ? 'Quiz results' : 'Quiz progress'}>
            {!round.complete && !round.statistics.answered ? <>0 of {round.statistics.questionCount} answered</> : <>{round.statistics.correct} out of {round.complete ? round.statistics.questionCount : round.statistics.answered} correct{!round.complete && ' so far'}</>} · {round.statistics.hintsUsed} hints used{typeof round.statistics.pointsEarned === 'number' && <> · {round.statistics.pointsEarned} points earned</>}
        </p>}
        {choosing && library ? <>
            <TextField select SelectProps={{ native: true }} fullWidth label='Choose a game' value={gameKey} disabled={busy} onChange={(event) => { setGameKey(event.target.value); nextRequest.current = null; }}>
                <option value=''>Choose a game</option>
                {library.games.filter((game) => game.available).map((game) => <option key={game.key} value={game.key}>{game.title}{library.featuredKey === game.key ? ' — Featured' : ''}</option>)}
            </TextField>
            {library.games.filter((game) => game.key === gameKey && game.available).map((game) => <div key={game.key}><p>{internals.brand(game.invitation, name)}</p><p>{internals.brand(game.introduction, name)}</p><p>{game.rules}</p><Button variant='contained' disabled={busy} onClick={() => { nextRequest.current = null; next({ gameKey: game.key }); }}>Play {game.title}</Button></div>)}
        </> : round?.complete ? <><p>{internals.brand(round.game?.closing, name) || 'You’ve tried all the available questions.'}</p><Button disabled={busy} onClick={() => next({ restart: true })}>Start again</Button></> : round && <>
            {round.imageClue && <Artwork src={ArtworkImages[round.imageClue.url] || round.imageClue.url} alt={round.imageClue.alt} referrerPolicy='no-referrer' onError={(event) => { event.currentTarget.alt = 'Artwork could not load. Please try again or choose another question.'; }} />}
            {round.emojiClue ? <>
                <p>{round.prompt.replace(round.emojiClue, '').trim()}</p>
                <EmojiClue role='img' aria-label={round.accessiblePrompt}>{internals.emojiSegments(round.emojiClue).map((emoji, index) => <span aria-hidden='true' key={index}>{emoji}</span>)}</EmojiClue>
            </> : <Prompt aria-label={round.accessiblePrompt}>{round.prompt}</Prompt>}
            {(round.hints || []).map((hint, index) => <p key={index}><strong>Hint {index + 1}:</strong> {hint}</p>)}
            {round.answer && <p><strong>Answer:</strong> {round.answer}</p>}
            {round.explanation && <p>{internals.brand(round.explanation, name)}</p>}
            {round.answer && round.imageClue?.title && <p>{round.imageClue.title} — {round.imageClue.artist}{round.imageClue.reference && <> · <a href={round.imageClue.reference} target='_blank' rel='noopener noreferrer'>About the artwork</a></>}</p>}
            {round.status === 'active' && <form onSubmit={(event) => { event.preventDefault(); action('guess'); }}>
                <TextField fullWidth label='Your answer' value={guess} disabled={busy} onChange={(event) => setGuess(event.target.value)} inputProps={{ maxLength: 200 }} />
                <Buttons><Button type='submit' variant='contained' disabled={busy || !guess.trim()}>Check answer</Button><Button disabled={busy || !round.hintsAvailable} onClick={() => action('hint')}>Hint</Button><Button disabled={busy} onClick={() => action('reveal')}>Reveal answer</Button></Buttons>
            </form>}
            <Button disabled={busy} onClick={() => next({ advance: true })}>Next question</Button>
        </>}
        {library && !choosing && <Button disabled={busy} onClick={() => { nextRequest.current = null; setChoosing(true); setFeedback(''); }}>Choose another game</Button>}
        <Button disabled={busy} onClick={closeGame}>Back to our conversation</Button>
        <p role='status'>{feedback}</p>
        <div ref={controlsEnd} />
    </Panel></DialogContent></Dialog>;
};

internals.Panel = Styled.section`padding: 4px 0; font-size: 16px;`;
internals.Buttons = Styled.div`display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px;`;
internals.Prompt = Styled.p`font-size: 24px; line-height: 1.6;`;
internals.EmojiClue = Styled.div`display: flex; flex-wrap: wrap; gap: 12px; font-size: 44px; line-height: 1.6; margin: 12px 0;`;
internals.Artwork = Styled.img`display: block; max-width: 100%; max-height: 320px; object-fit: contain; margin: 12px auto;`;
internals.brand = (value, name) => (value || '').replace(/\{name\}/g, name);
internals.emojiSegments = (value) => typeof Intl.Segmenter === 'function' ? [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(value)].map(({ segment }) => segment).filter((segment) => segment.trim()) : [value];
